import React from "react";
import { useQuill } from "react-quilljs";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { CommonService } from "../../../../utils/common.services";
import { FOLDER_TYPE, MAX_IMAGE_SIZE_MB, MAX_VIDEO_SIZE_MB } from "../../../../utils/contentTypes";
import toast from "react-hot-toast";
import {
  AllowedImageTypes,
  AllowedVideoTypes,
} from "../../../../utils/contentTypes";

const Embed = Quill.import("blots/embed");

const isAllowedMediaUrl = (url) =>
  typeof url === "string" &&
  (url.startsWith("blob:") ||
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:"));

const sanitizeMediaUrl = (url) => (isAllowedMediaUrl(url) ? url : "//:0");

const isRemoteMediaUrl = (url) =>
  typeof url === "string" &&
  (url.startsWith("http://") || url.startsWith("https://"));

const extractRemoteMediaUrls = (html = "") => {
  if (!html) return [];

  const doc = new DOMParser().parseFromString(html, "text/html");
  const urls = [];

  doc.querySelectorAll("img[src], video[src]").forEach((el) => {
    const src = el.getAttribute("src");
    if (isRemoteMediaUrl(src) && !urls.includes(src)) {
      urls.push(src);
    }
  });

  return urls;
};

class CustomImageBlot extends Embed {
  static blotName = "image";
  static tagName = "img";

  static create(value) {
    const node = super.create(value);
    node.setAttribute("src", value);
    node.style.width = "100%";
    node.style.display = "block";
    node.style.maxWidth = "100%";
    node.contentEditable = "false";
    return node;
  }

  static value(node) {
    return node.getAttribute("src");
  }

  static sanitize(url) {
    return sanitizeMediaUrl(url);
  }
}

class VideoBlot extends Embed {
  static blotName = "video";
  static tagName = "video";

  static create(value) {
    const node = super.create(value);
    node.setAttribute("controls", "");
    node.setAttribute("src", value);
    node.style.width = "100%";
    node.style.display = "block";
    node.style.maxWidth = "100%";
    return node;
  }

  static value(node) {
    return node.getAttribute("src");
  }

  static sanitize(url) {
    return sanitizeMediaUrl(url);
  }
}

Quill.register("formats/image", CustomImageBlot, true);
Quill.register("formats/video", VideoBlot, true);

const insertMediaAtCursor = (quill, type, src) => {
  const range = quill.getSelection(true);
  let index;

  if (range && range.index >= 0) {
    index = range.index;
    if (index > 0 && quill.getText(index - 1, 1) !== "\n") {
      quill.insertText(index, "\n", "user");
      index++;
    }
  } else {
    index = quill.getLength();
  }

  quill.insertEmbed(index, type, src, "user");
  quill.insertText(index + 1, "\n", "user");
  quill.setSelection(index + 2, 0);
};

const ArticleEditor = React.forwardRef(
  ({ value, onChange, error, maxLength }, ref) => {
    const onChangeRef = React.useRef(onChange);
    const quillInstanceRef = React.useRef(null);
    const localMediaRef = React.useRef(new Map());
    const lastEmittedHtmlRef = React.useRef("");
    const originalRemoteMediaRef = React.useRef(null);
    const removedRemoteMediaRef = React.useRef(new Set());

    React.useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const modules = React.useMemo(
      () => ({
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image", "video"],
          ["clean"],
        ],
      }),
      [],
    );

    const { quill, quillRef } = useQuill({
      theme: "snow",
      modules,
      formats: [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "list",
        "bullet",
        "link",
        "image",
        "video",
      ],
    });

    React.useEffect(() => {
      if (quill) {
        quillInstanceRef.current = quill;
      }
    }, [quill]);

    const pruneUnusedLocalMedia = React.useCallback((html) => {
      localMediaRef.current.forEach((_, blobUrl) => {
        if (!html.includes(blobUrl)) {
          URL.revokeObjectURL(blobUrl);
          localMediaRef.current.delete(blobUrl);
        }
      });
    }, []);

    const applyHtmlToEditor = React.useCallback((html) => {
      const editor = quillInstanceRef.current;
      if (!editor) return;

      const range = editor.getSelection();
      editor.setContents([]);
      editor.clipboard.dangerouslyPasteHTML(html || "");
      lastEmittedHtmlRef.current = editor.root.innerHTML;
      if (range) {
        editor.setSelection(range);
      }
    }, []);

    const registerLocalMedia = React.useCallback((file, mediaType) => {
      const blobUrl = URL.createObjectURL(file);
      localMediaRef.current.set(blobUrl, { file, mediaType });
      return blobUrl;
    }, []);

    const captureOriginalRemoteMedia = React.useCallback((html) => {
      const remoteUrls = extractRemoteMediaUrls(html);

      if (originalRemoteMediaRef.current === null) {
        originalRemoteMediaRef.current = remoteUrls;
        return;
      }

      // Edit content can arrive after the editor mounts with empty HTML.
      if (
        originalRemoteMediaRef.current.length === 0 &&
        remoteUrls.length > 0
      ) {
        originalRemoteMediaRef.current = remoteUrls;
      }
    }, []);

    const collectRemovedRemoteMedia = React.useCallback((html) => {
      const original = originalRemoteMediaRef.current || [];
      const remaining = new Set(extractRemoteMediaUrls(html));
      const removed = original.filter((url) => !remaining.has(url));
      removedRemoteMediaRef.current = new Set(removed);
      return removed;
    }, []);

    React.useImperativeHandle(ref, () => ({
      persistLocalMedia: async () => {
        const editor = quillInstanceRef.current;
        let content = editor?.root.innerHTML || "";

        pruneUnusedLocalMedia(content);

        const pending = Array.from(localMediaRef.current.entries()).filter(
          ([blobUrl]) => content.includes(blobUrl),
        );

        const persistResult = (nextContent) => ({
          content: nextContent,
          removedMediaUrls: collectRemovedRemoteMedia(nextContent),
        });

        if (!pending.length) {
          return persistResult(content);
        }

        const toastId = toast.loading("Uploading article media...");

        try {
          for (const [blobUrl, { file, mediaType }] of pending) {
            if (!content.includes(blobUrl)) {
              URL.revokeObjectURL(blobUrl);
              localMediaRef.current.delete(blobUrl);
              continue;
            }

            const uploadedUrl = await CommonService.uploadFileAndGetUrl(
              file,
              mediaType === "video"
                ? FOLDER_TYPE.ARTICLE_VIDEO
                : FOLDER_TYPE.ARTICLE_IMAGE,
              false,
            );

            content = content.split(blobUrl).join(uploadedUrl);
            URL.revokeObjectURL(blobUrl);
            localMediaRef.current.delete(blobUrl);
          }

          applyHtmlToEditor(content);
          onChangeRef.current(content);
          toast.dismiss(toastId);
          return persistResult(content);
        } catch (error) {
          applyHtmlToEditor(content);
          onChangeRef.current(content);
          toast.error("Failed to upload article media.", { id: toastId });
          throw error;
        }
      },
    }),
    [applyHtmlToEditor, collectRemovedRemoteMedia, pruneUnusedLocalMedia],
  );

    React.useEffect(() => {
      if (!quill) return;

      const toolbar = quill.getModule("toolbar");

      toolbar.addHandler("image", () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = AllowedImageTypes;
        input.click();

        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;

          const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];

          if (!allowedImageTypes.includes(file.type)) {
            toast.error("Only JPG, JPEG and PNG images are allowed.");
            return;
          }

          if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
            toast.error(`Image size should not exceed ${MAX_IMAGE_SIZE_MB} MB`);
            input.value = "";
            return;
          }

          const blobUrl = registerLocalMedia(file, "image");
          insertMediaAtCursor(quill, "image", blobUrl);
        };
      });

      toolbar.addHandler("video", () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = AllowedVideoTypes;
        input.click();

        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;

          if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
            toast.error(`Video size should not exceed ${MAX_VIDEO_SIZE_MB} MB`);
            input.value = "";
            return;
          }

          if (file.type !== "video/mp4") {
            toast.error("Only MP4 videos are allowed.");
            return;
          }

          const blobUrl = registerLocalMedia(file, "video");
          insertMediaAtCursor(quill, "video", blobUrl);
        };
      });
    }, [quill, registerLocalMedia]);

    React.useEffect(() => {
      if (!quill) return;

      const handleTextChange = () => {
        if (maxLength) {
          const textLength = Math.max(0, quill.getLength() - 1);

          if (textLength > maxLength) {
            quill.deleteText(maxLength, textLength - maxLength);
          }
        }

        const html = quill.root.innerHTML;
        pruneUnusedLocalMedia(html);
        collectRemovedRemoteMedia(html);
        lastEmittedHtmlRef.current = html;
        onChangeRef.current(html);
      };

      quill.on("text-change", handleTextChange);
      return () => {
        quill.off("text-change", handleTextChange);
      };
    }, [quill, maxLength, pruneUnusedLocalMedia, collectRemovedRemoteMedia]);

    React.useEffect(() => {
      if (!quill) return;

      const nextValue = value || "";
      const currentValue = quill.root.innerHTML;

      captureOriginalRemoteMedia(nextValue);

      if (
        nextValue === currentValue ||
        nextValue === lastEmittedHtmlRef.current
      ) {
        return;
      }

      applyHtmlToEditor(nextValue);
    }, [quill, value, applyHtmlToEditor, captureOriginalRemoteMedia]);

    React.useEffect(() => {
      const localMedia = localMediaRef.current;

      return () => {
        localMedia.forEach((_, blobUrl) => {
          URL.revokeObjectURL(blobUrl);
        });
        localMedia.clear();
      };
    }, []);

    return (
      <div
        style={{
          border: `2px solid ${error ? "#ef4444" : "#d8dbe1"}`,
          borderRadius: "10px",
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <div ref={quillRef} style={{ minHeight: "520px" }} />
      </div>
    );
  },
);

ArticleEditor.displayName = "ArticleEditor";

export default ArticleEditor;
